/**
 * FieldLookup — the Tech tab's 2nd tab. One search (WO#, title, customer, site),
 * grouped results, then read-mostly Customer / Site / Work Order pages.
 */
import { useEffect, useRef, useState } from "react";
import { tr } from "@/i18n";
import { useNavigate } from "react-router-dom";
import { Search, ChevronLeft, Building2, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { lookupSearch } from "@/lib/api";
import LookupClient from "@/components/tech/lookup/LookupClient";
import LookupSite from "@/components/tech/lookup/LookupSite";
import LookupWorkOrder from "@/components/tech/lookup/LookupWorkOrder";
import { Section, Empty, WORow, Row, fmtDate } from "@/components/tech/lookup/LookupParts";

const openMaps = (addr) => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`, "_blank");

export default function FieldLookup() {
  const navigate = useNavigate();
  const [q, setQ] = useState(() => sessionStorage.getItem("lookup_q") || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stack, setStack] = useState(() => { try { return JSON.parse(sessionStorage.getItem("lookup_stack") || "[]"); } catch { return []; } });
  const timer = useRef(null);

  useEffect(() => { sessionStorage.setItem("lookup_q", q); }, [q]);
  useEffect(() => { sessionStorage.setItem("lookup_stack", JSON.stringify(stack)); }, [stack]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { const r = await lookupSearch(q); setResults(r.data); }
      catch { toast.error("Search failed"); }
      finally { setLoading(false); }
    }, q ? 250 : 0);
    return () => clearTimeout(timer.current);
  }, [q]);

  const push = (type, id, label) => setStack(s => [...s, { type, id, label }]);
  const pop = () => setStack(s => s.slice(0, -1));
  const top = stack[stack.length - 1];
  const nav = {
    openClient: (id) => push("client", id, "Customer"),
    openSite: (id) => push("site", id, "Site"),
    openWO: (id) => push("wo", id, "Work Order"),
    openInspection: (id) => navigate(`/inspections/${id}`, { state: { returnTo: "/field" } }),
    openMaps,
  };

  return (
    <div className="max-w-[560px] mx-auto space-y-2" data-testid="field-lookup">
      <div className="flex items-center gap-2">
        {top ? (
          <button type="button" onClick={pop} className="inline-flex items-center gap-1 h-9 px-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="lookup-back">
            <ChevronLeft className="w-4 h-4" />{stack.length > 1 ? stack[stack.length - 2].label : "Search"}
          </button>
        ) : null}
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="search" value={q} onChange={(e) => { setQ(e.target.value); if (top) setStack([]); }} placeholder={tr("Search WO #, job, customer or site…")} data-testid="lookup-search"
            className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-brand" />
        </div>
      </div>

      {top?.type === "client" && <LookupClient clientId={top.id} {...nav} />}
      {top?.type === "site" && <LookupSite siteId={top.id} {...nav} />}
      {top?.type === "wo" && <LookupWorkOrder woId={top.id} {...nav} />}

      {!top && (
        <div className="space-y-2" data-testid="lookup-results">
          {loading && !results && <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-700" /></div>}
          {results?.recent && (
            <Section title={tr("Recently visited customers")} count={results.clients.length} testId="lookup-recent">
              {results.clients.length === 0 ? <Empty text="No recent visits yet — search above." /> : results.clients.map(c => (
                <Row key={c.id} onClick={() => nav.openClient(c.id)} testId={`lookup-client-${c.id}`}>
                  <div className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" /><span className="text-xs font-semibold text-slate-800 truncate">{c.name}</span></div>
                  <div className="text-[10px] text-slate-500 inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />Last visit {fmtDate(c.last_visit)}{c.city ? ` · ${c.city}` : ""}</div>
                </Row>
              ))}
            </Section>
          )}
          {results && !results.recent && (
            <>
              <Section title={tr("Customers")} count={results.clients.length} testId="lookup-clients">
                {results.clients.length === 0 ? <Empty text="No customers match." /> : results.clients.map(c => (
                  <Row key={c.id} onClick={() => nav.openClient(c.id)} testId={`lookup-client-${c.id}`}>
                    <div className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" /><span className="text-xs font-semibold text-slate-800 truncate">{c.name}</span></div>
                    {(c.city || c.state) && <div className="text-[10px] text-slate-500">{[c.city, c.state].filter(Boolean).join(", ")}</div>}
                  </Row>
                ))}
              </Section>
              <Section title={tr("Sites")} count={results.sites.length} testId="lookup-sites">
                {results.sites.length === 0 ? <Empty text="No sites match." /> : results.sites.map(s => (
                  <Row key={s.id} onClick={() => nav.openSite(s.id)} testId={`lookup-site-${s.id}`}>
                    <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" /><span className="text-xs font-semibold text-slate-800 truncate">{s.name}</span>{s.client_name && <span className="text-[10px] text-slate-400 truncate">· {s.client_name}</span>}</div>
                    <div className="text-[10px] text-slate-500 truncate">{s.address_full || "No address"}</div>
                  </Row>
                ))}
              </Section>
              <Section title={tr("Work Orders")} count={results.work_orders.length} testId="lookup-wos">
                {results.work_orders.length === 0 ? <Empty text="No work orders match." /> : results.work_orders.map(w => <WORow key={w.id} wo={w} onOpen={nav.openWO} showClient />)}
              </Section>
            </>
          )}
        </div>
      )}
    </div>
  );
}
